import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getGameState, sendTaskAnswer, sendTaskAnswersTimeLogsList, } from '../../../api/game-mode/game';
import { addTaskAnswer, selectCurrentTaskId, selectCurrentTaskInfo, selectCurrentTaskResult, selectCurrentTaskTimeLogs, selectGameResults, selectGameStatus, selectGameTasks, selectRallyInfo, setCurrentTaskId, setCurrentTaskTimeLogs, setGame, setTaskStatus, updateGameTotalPoints, } from '../../../store/slices/rallyGame';
import { GAME_DB } from '../../../db/game';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import { generateIdRoutePath, generateTaskRoutePath, getAtomFormattedDate } from '../../../helpers';
import { useAppDispatch } from '../../../store';
import { setIsModalOpen, setModalContent, setModalContentProps, } from '../../../store/slices/global';
import ReportIcon from '../../../assets/icons/flag.svg';
import GamePageLayout from '../../../layout/GamePageLayout';
import Footer from '../../../layout/Footer';
import { useValidator, } from '../../../hooks/formValidator/useValidator';
import { isAnswerSelected, isFileUploaded, isNotEmpty, } from '../../../hooks/formValidator/validators';
import GameplayControls from '../../../modules/GameControls/gameplayControls';
import useFetchRally from '../../../hooks/fetchData/fetchRally';
import useFetchGameState from '../../../hooks/fetchData/fetchGameState';
import useFetchGameTasks from '../../../hooks/fetchData/fetchGameTasks';
import { GAME_END_TIME } from '../../../app.config.vars/stored';
import Loader from '../../../ui/Loader';
import { API_ERRORS } from '../../../app.config.vars/errorCodes';
import GameTaskWrapper from '../../../modules/GameTask/GameTaskWrapper';
import { useTaskEndTimeTracker } from '../../../hooks/taskEndTimeTracker';
const inputTaskAnswerValidators = {
    inputTaskAnswer: [isNotEmpty],
};
const selectionTaskAnswerValidators = {
    selectionTaskAnswer: [isAnswerSelected],
    photoUploadTaskAnswer: [isFileUploaded],
};
const GamePlayPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { task: currentTaskNumber } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const rally = useSelector(selectRallyInfo);
    const gameStatus = useSelector(selectGameStatus);
    const gameTasks = useSelector(selectGameTasks);
    const gameTaskResults = useSelector(selectGameResults);
    const currentTaskId = useSelector(selectCurrentTaskId);
    const currentTaskInfo = useSelector(selectCurrentTaskInfo);
    const currentTaskResult = useSelector(selectCurrentTaskResult);
    const currentTaskTimeLogs = useSelector(selectCurrentTaskTimeLogs);
    const [showTaskResult, setShowTaskResult] = useState(false);
    const [currentSelectionTaskAnswers, setCurrentSelectionTaskAnswers] = useState(null);
    const [currentInputTaskAnswer, setCurrentInputTaskAnswer] = useState('');
    // Photo upload state holds the raw Blob + an object URL for preview + the
    // MIME type. We deliberately keep the full base64 string out of memory until
    // the moment of POST — on low-RAM Android devices, the base64 + canvas peak
    // was OOM-killing the renderer.
    const [currentPhotoUpload, setCurrentPhotoUpload] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    // Revoke any leftover object URL on unmount so the Blob can be GC'd.
    useEffect(() => {
        return () => {
            if (currentPhotoUpload?.previewUrl) {
                URL.revokeObjectURL(currentPhotoUpload.previewUrl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const replaceCurrentPhotoUpload = (next) => {
        setCurrentPhotoUpload((prev) => {
            if (prev?.previewUrl && prev.previewUrl !== next?.previewUrl) {
                URL.revokeObjectURL(prev.previewUrl);
            }
            return next;
        });
    };
    const { formErrors: taskAnswerValidationErrors, validateInputField, validateSelect, clearErrors, } = useValidator({
        inputSchema: inputTaskAnswerValidators,
        selectSchema: selectionTaskAnswerValidators,
    });
    useFetchRally(id);
    useFetchGameState();
    useFetchGameTasks();
    useTaskEndTimeTracker();
    useEffect(() => {
        const handleUnsavedGame = async () => {
            const unsavedGame = !!localStorage.getItem(GAME_END_TIME);
            if (unsavedGame) {
                navigate(generateIdRoutePath(APP_ROUTES.rallyGameResult, id));
            }
        };
        handleUnsavedGame();
    }, []);
    useEffect(() => {
        if (currentTaskNumber && +currentTaskNumber) {
            if (gameTasks.length) {
                const currentTaskId = gameTasks.find((task) => {
                    return task.sorting === +currentTaskNumber;
                })?.uid;
                if (currentTaskId) {
                    dispatch(setCurrentTaskId(currentTaskId));
                    clearErrors();
                }
            }
        }
    }, [currentTaskNumber, gameTasks]);
    useEffect(() => {
        const updateTaskChange = async () => {
            if (currentTaskInfo && currentTaskResult) {
                if (currentTaskInfo.taskType === 'action' &&
                    (currentTaskInfo.answerType === 'single-selection' ||
                        currentTaskInfo.answerType === 'multiple-selection' ||
                        currentTaskInfo.answerType === 'player-selection')) {
                    if (currentTaskResult.status === 'completed') {
                        const lastAnswer = currentTaskResult.playerAnswers[currentTaskResult.playerAnswers.length - 1];
                        const selectedAnswersUid = lastAnswer.answerValue
                            ? lastAnswer.answerValue.split(',').map((uid) => uid.trim())
                            : null;
                        setCurrentSelectionTaskAnswers(currentTaskInfo.answers.map((answer) => ({
                            uid: answer.uid,
                            content: answer.content,
                            isSelected: !!selectedAnswersUid && selectedAnswersUid.includes(answer.uid.toString()),
                        })));
                    }
                    else {
                        setCurrentSelectionTaskAnswers(currentTaskInfo.answers.map((answer) => ({
                            uid: answer.uid,
                            content: answer.content,
                            isSelected: false,
                        })));
                    }
                }
                else if (currentTaskInfo.taskType === 'action' &&
                    (currentTaskInfo.answerType === 'text' || currentTaskInfo.answerType === 'validated-text')) {
                    if (currentTaskResult.status === 'completed' ||
                        (currentTaskResult.status === 'failed' &&
                            currentTaskInfo.taskType === 'action' &&
                            currentTaskResult.answerType === 'validated-text')) {
                        const lastAnswer = currentTaskResult.playerAnswers[currentTaskResult.playerAnswers.length - 1];
                        setCurrentInputTaskAnswer(lastAnswer.answerValue ? lastAnswer.answerValue : '');
                    }
                    else {
                        setCurrentInputTaskAnswer('');
                    }
                }
                else if (currentTaskInfo.taskType === 'action' &&
                    currentTaskInfo.answerType === 'upload') {
                    if (currentTaskResult.status === 'completed') {
                        const stored = await GAME_DB.GET.taskUploadBlob(currentTaskId);
                        if (stored) {
                            replaceCurrentPhotoUpload({
                                blob: stored.blob,
                                previewUrl: URL.createObjectURL(stored.blob),
                                mimeType: stored.mimeType,
                            });
                        }
                        else {
                            replaceCurrentPhotoUpload(null);
                        }
                    }
                    else {
                        replaceCurrentPhotoUpload(null);
                    }
                }
                else if (currentTaskInfo.taskType === 'check-in' &&
                    currentTaskInfo.answerType === 'location') {
                    if (currentTaskResult.status === 'completed') {
                        setShowTaskResult(true);
                    }
                }
            }
        };
        updateTaskChange();
    }, [currentTaskInfo, currentTaskResult]);
    const handleSelectionTaskAnswerButtonClick = (answerId) => {
        switch (currentTaskInfo.answerType) {
            case 'single-selection':
            case 'player-selection':
                setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => ({
                    ...answer,
                    isSelected: answer.uid === answerId,
                })));
                break;
            case 'multiple-selection':
                const currentAnswer = currentSelectionTaskAnswers?.find((answer) => answer.uid === answerId);
                if (currentAnswer) {
                    if (currentAnswer.isSelected) {
                        if (currentSelectionTaskAnswers
                            .filter((answer) => answer.uid !== answerId)
                            .find((answer) => answer.isSelected)) {
                            setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => {
                                return answer.uid !== answerId ? answer : { ...answer, isSelected: false };
                            }));
                        }
                    }
                    else {
                        setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => {
                            return answer.uid !== answerId ? answer : { ...answer, isSelected: true };
                        }));
                    }
                }
                break;
        }
    };
    const handleNavButtonClick = async (chosenTaskNumber) => {
        setShowTaskResult(false);
        setShowLoader(true);
        if (currentTaskResult?.status !== 'completed') {
            const updateLocalData = async () => {
                if (currentTaskResult?.status === 'new') {
                    await GAME_DB.UPDATE.taskStatus({
                        uid: currentTaskId,
                        status: currentTaskInfo?.taskType === 'information' ? 'completed' : 'skipped',
                    });
                    dispatch(setTaskStatus({
                        taskUid: currentTaskId,
                        status: currentTaskInfo?.taskType === 'information' ? 'completed' : 'skipped',
                    }));
                }
            };
            if (currentTaskTimeLogs) {
                const response = await sendTaskAnswersTimeLogsList({
                    timeLogs: [
                        {
                            task: currentTaskTimeLogs.taskUid,
                            startTime: currentTaskTimeLogs.startTime,
                            endTime: getAtomFormattedDate(Date.now()),
                        },
                    ],
                });
                if (response.error) {
                    await GAME_DB.UPDATE.pushAnswerTimeLogs({
                        task: currentTaskTimeLogs.taskUid,
                        startTime: currentTaskTimeLogs.startTime,
                        endTime: getAtomFormattedDate(Date.now()),
                    });
                    await updateLocalData();
                }
                else {
                    const gameState = await getGameState();
                    if (gameState.data) {
                        await GAME_DB.UPDATE.gameState(gameState.data);
                        dispatch(setGame(gameState.data));
                    }
                    else {
                        await updateLocalData();
                    }
                }
                dispatch(setCurrentTaskTimeLogs(null));
            }
        }
        setShowLoader(false);
        navigate(generateTaskRoutePath(APP_ROUTES.rallyGamePlay, id, chosenTaskNumber));
    };
    const goToNextTask = () => {
        const nextAvailableTask = gameTaskResults.find((task) => task.status !== 'completed' && task.sorting > +currentTaskNumber);
        if (nextAvailableTask) {
            navigate(generateTaskRoutePath(APP_ROUTES.rallyGamePlay, id, nextAvailableTask.sorting));
        }
        else {
            navigate(generateIdRoutePath(APP_ROUTES.rallyGameResult, id));
        }
    };
    const handleInputTaskAnswerChange = (event) => {
        if (currentTaskResult?.status !== 'completed') {
            setCurrentInputTaskAnswer(event.currentTarget.value);
        }
    };
    const handlePhotoUploadTaskAnswerChange = (file) => {
        if (currentTaskResult?.status !== 'completed') {
            if (file) {
                // No FileReader / no base64 here — keep the heavy payload as a Blob and
                // preview via an object URL. Conversion to base64 happens only at POST
                // time, in a single short-lived step. This was the main fix for the
                // OOM-on-capture seen on low-RAM Android devices.
                replaceCurrentPhotoUpload({
                    blob: file,
                    previewUrl: URL.createObjectURL(file),
                    mimeType: file.type || 'application/octet-stream',
                });
            }
            else {
                replaceCurrentPhotoUpload(null);
            }
        }
    };
    const updateLocalLocationTaskResult = async (userPosition) => {
        await GAME_DB.UPDATE.pushLocalAnswer({
            task: currentTaskId,
            answerType: currentTaskInfo.answerType,
            coordinatesLat: userPosition.lat,
            coordinatesLon: userPosition.lng,
        });
        await GAME_DB.UPDATE.taskResult({
            taskId: currentTaskId,
            status: 'completed',
            pointsEarned: currentTaskInfo.successPoints,
            newAnswer: {
                coordinatesLat: userPosition.lat,
                coordinatesLon: userPosition.lng,
                pointsEarned: currentTaskInfo.successPoints,
            },
        });
        dispatch(setTaskStatus({
            taskUid: currentTaskId,
            status: 'completed',
        }));
        dispatch(addTaskAnswer({
            taskUid: currentTaskId,
            answer: {
                coordinatesLat: userPosition.lat,
                coordinatesLon: userPosition.lng,
                pointsEarned: currentTaskInfo.successPoints,
            },
        }));
        dispatch(updateGameTotalPoints({ lastEarnedPoints: currentTaskInfo.successPoints }));
    };
    const handleLocationTaskCoordinatesSubmit = async (userPosition) => {
        const response = await sendTaskAnswer({
            task: currentTaskInfo.uid,
            answerType: currentTaskInfo.answerType,
            coordinatesLat: userPosition.lat,
            coordinatesLon: userPosition.lng,
        });
        if (response.error?.code === API_ERRORS.noInternet) {
            await updateLocalLocationTaskResult(userPosition);
        }
        else {
            const gameState = await getGameState();
            if (gameState.data) {
                await GAME_DB.UPDATE.gameState(gameState.data);
                dispatch(setGame(gameState.data));
            }
            else {
                await updateLocalLocationTaskResult(userPosition);
            }
        }
        setShowTaskResult(true);
    };
    const handleTargetLocationCheckedIn = async (userPosition) => {
        if (currentTaskTimeLogs) {
            const timeLogs = {
                task: currentTaskTimeLogs.taskUid,
                startTime: currentTaskTimeLogs.startTime,
                endTime: getAtomFormattedDate(Date.now()),
            };
            const response = await sendTaskAnswersTimeLogsList({
                timeLogs: [timeLogs],
            });
            if (response.error) {
                await GAME_DB.UPDATE.pushAnswerTimeLogs(timeLogs);
            }
            dispatch(setCurrentTaskTimeLogs(null));
        }
        await handleLocationTaskCoordinatesSubmit(userPosition);
    };
    const handleReportProblem = () => {
        dispatch(setIsModalOpen(true));
        dispatch(setModalContent('reportProblem'));
        dispatch(setModalContentProps({ rallyId: rally.uid, taskId: currentTaskId }));
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'page-content-wrapper rally-game-page', children: rally && currentTaskId && currentTaskInfo && currentTaskResult ? (_jsx(GamePageLayout, { id: id, onNavButtonClick: handleNavButtonClick, rally: rally, showGameResult: false, controls: _jsxs(_Fragment, { children: [_jsx(GameplayControls, { showTaskResult: showTaskResult, currentInputTaskAnswer: currentInputTaskAnswer, currentSelectionTaskAnswers: currentSelectionTaskAnswers, currentPhotoUploadBlob: currentPhotoUpload?.blob ?? null, currentPhotoUploadMimeType: currentPhotoUpload?.mimeType ?? null, currentTaskId: currentTaskId, currentTaskInfo: currentTaskInfo, currentTaskResult: currentTaskResult, setShowTaskResult: setShowTaskResult, goToNextTask: goToNextTask, rallyPathId: id, isLinearRally: rally.isLinear, gameStatus: gameStatus, validateInputField: validateInputField, validateSelect: validateSelect, showLoader: showLoader, setShowLoader: setShowLoader }), _jsxs("div", { className: "report-problem-link", onClick: handleReportProblem, children: [_jsx(ReportIcon, {}), _jsx("span", { children: t('report.link_text') })] })] }), children: _jsx(GameTaskWrapper, { pathId: id, isLinearRally: rally.isLinear, taskInfo: currentTaskInfo, taskResult: currentTaskResult, selectionTaskAnswers: currentSelectionTaskAnswers, onSelectionTaskAnswerButtonClick: handleSelectionTaskAnswerButtonClick, inputTaskAnswer: currentInputTaskAnswer, onInputTaskAnswerChange: handleInputTaskAnswerChange, uploadPhotoTaskAnswer: currentPhotoUpload?.previewUrl ?? null, uploadPhotoTaskAnswerMimeType: currentPhotoUpload?.mimeType ?? null, onUploadPhotoTaskAnswerChange: handlePhotoUploadTaskAnswerChange, onTargetLocationCheckedIn: (userPosition) => handleTargetLocationCheckedIn(userPosition), showTaskResult: showTaskResult, taskAnswerValidationErrors: taskAnswerValidationErrors, showLoader: showLoader }) })) : (_jsx(Loader, {})) }), _jsx(Footer, { layout: "gameplay" })] }));
};
export default GamePlayPage;
